import { IListModelQueryParamValues, IModelJsonValues } from '@sparelabs/http-client-utils'

/**
 * In practice, this is a combination of matching context types and manual intervention types,
 * with the addition of BackgroundMatching. Deep search is identified separately because it
 * can apply to several of these types.
 */
export enum RequestHistoryContextType {
  RequestInsertion = 'requestInsertion',
  TargetedOptimization = 'targetedOptimization',
  DutyOptimization = 'dutyOptimization',
  GlobalOptimization = 'globalOptimization',
  LiveGlobalOptimization = 'liveGlobalOptimization',
  TriggeredOptimization = 'triggeredOptimization',
  BreakInsertion = 'breakInsertion',

  // Manual interventions: from duty modifications
  ForceMatch = 'forceMatch',
  CompletePickup = 'completePickup',
  CompleteDropoff = 'completeDropoff',
  ReversePickup = 'reversePickup',
  ReverseDropoff = 'reverseDropoff',
  ReverseNoShow = 'reverseNoShow',
  EditDropoff = 'editDropoff',
}

/**
 * Metadata for the request history:
 * - Deep search is identified here when relevant because it can apply to several of the context types.
 * - Break is relevant for BreakInsertion.
 * - Original duty is relevant when the request moves from one duty to another.
 */
export interface IRequestHistoryMetadata {
  isDeepSearch?: boolean
  breakId?: string
  originalDutyId?: string
}

export interface IRequestHistoryResponse extends IModelJsonValues {
  id: string
  dutyId: string
  requestId: string
  contextType: RequestHistoryContextType
  pickupId: string | null
  pickupScheduledTs: number | null
  dropoffId: string | null
  dropoffScheduledTs: number | null
  authorId: string | null
  dutyDelayId: string | null
  /**
   * The driverBehaviourId is not persisted directly in the request history table,
   * but bulk loaded in the operations class.
   */
  driverBehaviourId: string | null
  requestConstraintOverrideId: string | null
  metadata: IRequestHistoryMetadata
}

export interface IRequestHistoryListQueryParams extends IListModelQueryParamValues {
  requestId: string
}

// get request histories
export interface IRequestHistoriesResponse {
  pickupHistories: IRequestHistoryResponse[]
  dropoffHistories: IRequestHistoryResponse[]
  compareHistories: IRequestHistoryResponse[]
}

// get request history summary
export interface IRequestHistorySummaryResponse {
  summaries: {
    pickup: IRequestHistorySummary
    dropoff: IRequestHistorySummary
    overall: IRequestHistorySummary
  }
  generatedAt: number
  modelVersion: string
}

export interface IRequestHistorySummary {
  summary: string
  keyEvents: IRequestHistoryKeyEvent[]
}

export interface IRequestHistoryKeyEvent {
  timestamp: number
  description: string
  contextType: RequestHistoryContextType
  isManualIntervention: boolean
}

export interface IEnrichedRequestData {
  request: {
    id: string
    pickupAddress: string
    dropoffAddress: string
    riderId: string
    riderName: string
  }
  dutyMap: Map<string, {
    identifier: string
    driverName: string
  }>
  authorMap: Map<string, string>
}
